import { useCallback, useEffect, useRef } from "react";
import lodash from "lodash";
import { processDateDetection, DateDetectionCallbacks } from "../helpers/debouncedDateDetection";

interface UseDebouncedDateDetectionParams {
  patientDob: string;
  setPatientDob: (value: string) => void;
  setInputText: (value: string) => void;
  setSearchText: (value: string) => void;
  debounceDelay?: number;
}

interface UseDebouncedDateDetectionReturn {
  debouncedDateDetection: (value: string) => void;
  debounceSearch: lodash.DebouncedFunc<(value: string) => void>;
}

/**
 * Custom hook for debounced date detection in search inputs
 * Handles date parsing, formatting, and query text management
 * 
 * @param params - Configuration object containing state setters and current patientDob
 * @returns Object containing debouncedDateDetection function and debounceSearch function
 */
export function useDebouncedDateDetection({
  patientDob,
  setPatientDob,
  setInputText,
  setSearchText,
  debounceDelay = 700,
}: UseDebouncedDateDetectionParams): UseDebouncedDateDetectionReturn {
  // Use refs to access current values without causing debounce recreation
  const patientDobRef = useRef(patientDob);
  const callbacksRef = useRef<DateDetectionCallbacks>({ 
    setPatientDOB: setPatientDob, 
    setInputText, 
    setSearchText 
  });
  
  useEffect(() => {
    patientDobRef.current = patientDob;
  }, [patientDob]);
  
  useEffect(() => {
    callbacksRef.current = { 
      setPatientDOB: setPatientDob, 
      setInputText, 
      setSearchText 
    };
  }, [setPatientDob, setInputText, setSearchText]);

  // Debounced search function for text queries
  const debounceSearch = useCallback(
    lodash.debounce((value: string) => {
      callbacksRef.current.setSearchText?.(value);
    }, debounceDelay),
    [debounceDelay]
  );

  // Debounced date detection - only runs after user stops typing
  // Don't include patientDob or callbacks in deps to avoid recreating debounce on state change
  const debouncedDateDetection = useCallback(
    lodash.debounce((value: string) => {
      processDateDetection(value, callbacksRef.current, {
        currentPatientDOB: patientDobRef.current,
        debouncedSearch: debounceSearch,
      });
    }, debounceDelay),
    [debounceSearch, debounceDelay]
  );

  return {
    debouncedDateDetection,
    debounceSearch,
  };
}

